import { useState, useEffect, useCallback } from "react";
import SUBSCRIPTION from "../api/services/SUBSCRIPTION";
import BILLING from "../api/services/BILLING";

/**
 * Hook to check business capabilities:
 * - isPro: whether the business is on a paid (non-FREE) plan
 * - hasClearingSetup: whether acquiring (Hyp) + an invoice provider
 *   (Morning) are configured. ZCredit, Grow and EZcount are retired — only the
 *   legacy stored value is still recognised, in paymentSettings, to migrate
 *   businesses off it.
 * - planMode: the raw plan mode string ("FREE", "PRO", etc.)
 * - planName: display name of the current plan
 * - loading: whether the checks are still loading
 *
 * Usage:
 *   const { isPro, hasClearingSetup, loading } = useBusinessCapabilities();
 *   if (!isPro) return <ProUpgradePrompt />;
 */
export const useBusinessCapabilities = () => {
  const [isPro, setIsPro] = useState(false);
  const [hasClearingSetup, setHasClearingSetup] = useState(false);
  const [planMode, setPlanMode] = useState("FREE");
  const [planName, setPlanName] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchCapabilities = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, credRes] = await Promise.allSettled([
        SUBSCRIPTION.get_sub_details(),
        BILLING.get_payment_cred(),
      ]);

      // Plan check
      if (subRes.status === "fulfilled") {
        const plan = subRes.value?.data?.currentPlan;
        if (plan) {
          const mode = plan.planMode || "FREE";
          setPlanMode(mode);
          setPlanName(plan.displayTitle || plan.title || "");
          setIsPro(mode !== "FREE");
        }
      }

      // Clearing check
      if (credRes.status === "fulfilled") {
        const creds = credRes.value?.data?.data || credRes.value?.data || {};
        const hasClearing = !!(
          creds.terminalNumber ||
          creds.greenInvoiceApiKey
        );
        setHasClearingSetup(hasClearing);
      }
    } catch (err) {
      console.log("useBusinessCapabilities error:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCapabilities();
  }, [fetchCapabilities]);

  return {
    isPro,
    hasClearingSetup,
    canUseBilling: isPro && hasClearingSetup,
    planMode,
    planName,
    loading,
    refetch: fetchCapabilities,
  };
};

/**
 * Non-hook version for one-time checks outside React components.
 * Returns a promise with the same shape.
 */
export const checkBusinessCapabilities = async () => {
  try {
    const [subRes, credRes] = await Promise.allSettled([
      SUBSCRIPTION.get_sub_details(),
      BILLING.get_payment_cred(),
    ]);

    let isPro = false;
    let planMode = "FREE";
    if (subRes.status === "fulfilled") {
      const plan = subRes.value?.data?.currentPlan;
      if (plan) {
        planMode = plan.planMode || "FREE";
        isPro = planMode !== "FREE";
      }
    }

    let hasClearingSetup = false;
    if (credRes.status === "fulfilled") {
      const creds = credRes.value?.data?.data || credRes.value?.data || {};
      hasClearingSetup = !!(creds.terminalNumber || creds.greenInvoiceApiKey);
    }

    return {
      isPro,
      hasClearingSetup,
      canUseBilling: isPro && hasClearingSetup,
      planMode,
    };
  } catch (err) {
    console.log("checkBusinessCapabilities error:", err);
    return {
      isPro: false,
      hasClearingSetup: false,
      canUseBilling: false,
      planMode: "FREE",
    };
  }
};
